import { useNodeConnections, useStore } from "@xyflow/react";
import { useMemo } from "react";
import { BlocksType } from "./types";

interface UsePortNameProps {
  port: any;
  edges: any[];
  nodeId: string;
  direct?: boolean;
}

export function usePortName({ port, edges, nodeId, direct }: UsePortNameProps) {
  // Always call hooks at the top level
  const connections = useNodeConnections({
    handleType: "target",
    handleId: port?.id,
  });

  // Get first connection
  const connection = connections?.[0];
  const connectedNodeId = connection?.source;

  // Subscribe to connected node so updates trigger a re-render
  const connectedNode = useStore((state) =>
    connectedNodeId ? state.nodes.find((n) => n.id === connectedNodeId) : null,
  );

  return useMemo(() => {
    if (!port) {
      return {
        name: "Input",
        singleEdge: false,
        connectedNode: null,
        connectedNodeData: null,
        period: null,
      };
    }

    const ids = port.id;
    const connectedData = connectedNode?.data || {};

    const outputs = Array.isArray(connectedData.outputs)
      ? connectedData.outputs
      : [];
    const otherInputs = Array.isArray(connectedData.other_inputs)
      ? connectedData.other_inputs
      : [];

    const connectedNodeData = outputs.find(
      (item: any) => item?.id === connection?.sourceHandle,
    );

    const period = otherInputs.find(
      (i: any) => i?.name === "period" || i?.name === "Number",
    )?.value;

    const loopback = otherInputs.find(
      (i: any) => i?.name === "Lookback",
    )?.value;

    const number = otherInputs.find((i: any) => i?.name === "Number")?.value;

    const singleEdge = edges?.some(
      (item) => item?.target === nodeId && item?.targetHandle === ids,
    );
    let name = port?.name || "";

    if (direct && (connectedData as any)?.title) {
      // Fallback to getConnectedName logic
      const extra = (connectedData as any)?.outputs_list?.[
        outputs.findIndex((i: any) => i?.id === connection?.sourceHandle)
      ]?.split("_");

      const extraText = extra?.length > 1 ? `_${extra[1]?.toUpperCase()}` : "";

      let sourceNameAbbrv =
        (connectedData as any)?.title
          ?.replace("/", " ")
          .split(" ")
          .map((i: any) => i[0])
          .join("") ??
        (port?.name || "");

      name = `${sourceNameAbbrv}${extraText}${period ? `[${period}]` : ""}`;
    }
    if (connectedData.name == "Number") {
      name = `N${number ? ` [${number}]` : ""}`;
    } else if (connectedData.type === BlocksType.DATA) {
      name = `${connectedData.name}${loopback ? ` [${loopback}]` : ""}`;
    } else if (
      ["Close Price", "Current Price", "Open Price", "High Price"].includes(
        connectedData?.name as string,
      )
    ) {
      name = `${connectedData.name}${loopback ? ` [${loopback}]` : ""}`;
    } else if (connectedNodeData?.name) {
      name = `${connectedNodeData.name.toUpperCase()}${
        period ? ` [${period}]` : ""
      }`;
    }

    return {
      name,
      singleEdge,
      connectedNode,
      connectedNodeData,
      period,
      connectedNodeName: connectedData.name,
      connectedNodeType: connectedData.type,
    };
  }, [port, edges, nodeId, connection, connectedNode]);
}
